import {
  APPLICATION_PROPOSAL_STATUS,
} from '../../../../../config/constants'

import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import moment from 'moment'

import ProposalServiceLinesDisplay from './proposal-service-lines-display'
import ProposalServicesTotalSummary from './proposal-services-total-summary'

export function formatProposalAmount({ amount, currencyCode }: ApplicationProposalAmountArgs): string | null {
  if (!amount?.trim()) return null
  const normalized = amount.replace(/\s/g, '').replace(',', '.')
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) {
    return `${amount} ${currencyCode}`.trim()
  }
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric)
  return `${formatted} ${currencyCode}`
}

const ProposalDetailBlock = ({
  label,
  children,
  managerView = false,
}: ApplicationProposalDetailBlockProps) => (
  <Stack spacing={0.5}>
    <Typography variant='caption' component='p' color={managerView ? 'text.primary' : 'text.secondary'}>
      {label}
    </Typography>
    <Typography color={managerView ? 'text.primary' : undefined}
      variant='body2'
      component='div'
      sx={{
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </Typography>
  </Stack>
)

const ExecutorProposalCard = ({
  proposal,
  servicesComparison,
  declineReasonFallback = '',
  managerView = false,
}: ApplicationExecutorProposalCardProps) => {
  const { t } = useTranslation()
  const isDeclined = Boolean(proposal.declined_at)
  const declineReason = (proposal.decline_reason || declineReasonFallback || '').trim()
  const declinedAtLabel = proposal.declined_at
    ? moment(proposal.declined_at).format('DD.MM.YYYY HH:mm')
    : null
  const totalComparison = servicesComparison?.total ?? {
    before: proposal.original_calculation_amount,
    after: proposal.amount,
    delta: null,
  }
  const hasTotal = Boolean(totalComparison.after?.trim() || totalComparison.before?.trim())
  const hasServiceLines = Boolean(proposal.service_lines?.length)
  const showTotal = hasTotal && (!isDeclined || hasServiceLines)
  const showStatus = !isDeclined && proposal.status !== APPLICATION_PROPOSAL_STATUS.PUBLISHED
  const validUntil = proposal.valid_until
    ? moment(proposal.valid_until).format('DD.MM.YYYY')
    : null
  const hasDetails = [
    proposal.services_description,
    proposal.included_description,
    proposal.excluded_description,
    proposal.terms,
  ].some((value) => value?.trim())
  const hasContent = hasServiceLines || hasDetails
  const hasBody = hasContent || Boolean(validUntil)

  return (
    <Paper
      component='article'
      variant='outlined'
      square
      className='application-executor-proposal-card'
      sx={{
        px: 3,
        py: managerView ? 2 : 3,
        borderColor: isDeclined ? 'error.main' : 'divider',
      }}
    >
      <Stack
        component='header'
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        justifyContent='space-between'
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        spacing={2}
      >
        <Box sx={{
          minWidth: 0,
        }}>
          <Stack direction='row' flexWrap='wrap' alignItems='center' gap={1}>
            <Typography
              variant='h6'
              component='p'
              sx={{
                overflowWrap: 'anywhere',
              }}
            >
              {proposal.company_name || '—'}
            </Typography>
            {isDeclined && (
              <Chip size='small' color='error' label={t('applications__text__proposalDeclinedLabel')} />
            )}
            {showStatus && proposal.status_label && (
              <Chip size='small' label={proposal.status_label} />
            )}
          </Stack>
          {showTotal && (
            <Tooltip title={t('applications__text__proposalServicesVsFullTotalHint')}>
              <Typography variant='caption' component='p' color={managerView ? 'text.primary' : 'text.secondary'}>
                {t('applications__text__proposalHeaderServicesTotal')}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {showTotal && (
          <ProposalServicesTotalSummary
            comparison={totalComparison}
            currencyCode={proposal.currency_code}
          />
        )}
      </Stack>
      {proposal.change_reason?.trim() && (
        <Typography color={managerView ? 'text.primary' : undefined}
          variant='body2'
          sx={{
            mt: 1,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {proposal.change_reason}
        </Typography>
      )}
      {hasBody && <Divider sx={{
        mt: 2,
        mb: hasServiceLines ? 0 : 2,
      }} />}
      {hasServiceLines && (
        <ProposalServiceLinesDisplay
          serviceLines={proposal.service_lines!}
          currencyCode={proposal.currency_code}
          comparison={servicesComparison}
          managerView={managerView}
        />
      )}
      {hasDetails && (
        <Stack spacing={2} sx={{
          mt: hasServiceLines ? 2 : 0,
        }}>
          {proposal.services_description?.trim() && (
            <ProposalDetailBlock managerView={managerView} label={t('applications__text__proposalServices')}>
              {proposal.services_description}
            </ProposalDetailBlock>
          )}
          {proposal.included_description?.trim() && (
            <ProposalDetailBlock managerView={managerView} label={t('applications__text__proposalIncluded')}>
              {proposal.included_description}
            </ProposalDetailBlock>
          )}
          {proposal.excluded_description?.trim() && (
            <ProposalDetailBlock managerView={managerView} label={t('applications__text__proposalExcluded')}>
              {proposal.excluded_description}
            </ProposalDetailBlock>
          )}
          {proposal.terms?.trim() && (
            <ProposalDetailBlock managerView={managerView} label={t('applications__text__proposalTerms')}>
              {proposal.terms}
            </ProposalDetailBlock>
          )}
        </Stack>
      )}
      {validUntil && (
        <Typography
          variant='caption'
          component='p'
          color={managerView ? 'text.primary' : 'text.secondary'}
          sx={{
            mt: hasContent ? 2 : 0,
          }}
        >
          {t('applications__text__proposalValidUntil')}: {validUntil}
        </Typography>
      )}
      {isDeclined && (
        <Stack component='footer' spacing={1} sx={{
          mt: 2,
        }}>
          <Divider />
          <Typography variant='subtitle2' component='p' color='error.main'>
            {t(managerView
              ? 'applications__text__proposalDeclinedByClient'
              : 'applications__text__proposalDeclined')}
          </Typography>
          {declinedAtLabel && (
            <Typography variant='caption' component='p' color={managerView ? 'text.primary' : 'text.secondary'}>
              {t('applications__text__proposalDeclinedAt', { date: declinedAtLabel })}
            </Typography>
          )}
          {declineReason && (
            <ProposalDetailBlock managerView={managerView} label={t('applications__text__declineReason')}>
              {declineReason}
            </ProposalDetailBlock>
          )}
        </Stack>
      )}
    </Paper>
  )
}

export default ExecutorProposalCard
